import {
  sentryGlobalFunctionMiddleware,
  sentryGlobalRequestMiddleware,
} from "@sentry/tanstackstart-react";
import { createCsrfMiddleware, createStart } from "@tanstack/react-start";
import { apiErrorSerializationAdapter } from "#/modules/util.auth/api-errors";
import { notFoundMiddleware } from "#/modules/util.errors/middleware.not-found";
import { serverUnavailableMiddleware } from "#/modules/util.errors/middleware.server-unavailable";
import { tenantContextMiddleware } from "#/modules/util.fetch-client/middleware.tenant";
import { requestLogMiddleware } from "#/modules/util.instrumentation/middleware.request-log";
import { serverFnLogMiddleware } from "#/modules/util.instrumentation/middleware.server-fn-log";
import { SENTRY_TUNNEL_PATH } from "#/modules/util.instrumentation/sentry";
import { resolvePublicOrigin } from "#/modules/util.instrumentation/public-origin";
import { sentryUserMiddleware } from "#/modules/util.instrumentation/middleware.sentry-user";
import { securityHeadersMiddleware } from "#/modules/util.security/middleware.security-headers";
import { versionSkewMiddleware } from "#/modules/util.instrumentation/middleware.version-skew";
import { serverTimingMiddleware } from "#/modules/util.instrumentation/middleware.server-timing";
import { unknownServerFnMiddleware } from "#/modules/util.instrumentation/middleware.unknown-server-fn";

// Reject cross-site server function requests, and cross-site mutations against server-route
// handlers (e.g. api/$school/file-upload POST). `handlerType` is only `"serverFn" | "router"` —
// "router" also covers ordinary GET page navigations (which can arrive with
// `Sec-Fetch-Site: none` on a typed URL/bookmark), so we only widen to non-GET/HEAD "router"
// requests to avoid blocking normal navigation while still covering server-route mutations.
// Browsers without `Sec-Fetch-Site` (Safari < 16.4) fall back to the Origin check, which must
// compare against the public origin rather than the proxied `request.url`.
// The Sentry tunnel is exempt: it changes no user state and is the channel every client error is
// reported through, so it must not depend on the check it would have to report on.
const csrfMiddleware = createCsrfMiddleware({
  filter: (ctx) =>
    ctx.handlerType === "serverFn" ||
    (ctx.handlerType === "router" &&
      ctx.request.method !== "GET" &&
      ctx.request.method !== "HEAD" &&
      ctx.pathname !== SENTRY_TUNNEL_PATH),
  origin: (origin, ctx) => origin === resolvePublicOrigin(ctx.request),
});

// App-wide "data-only" SSR: loaders/beforeLoad run on server; route components render on client.
// The Sentry middleware must stay first in both arrays so the request/server-fn span wraps the
// middleware that follows it (and so errors thrown in those are captured).
export const startInstance = createStart(() => ({
  defaultSsr: "data-only",
  serializationAdapters: [apiErrorSerializationAdapter],
  // The user middleware runs inside the Sentry request scope so every log line and event of the
  // request carries the signed-in user. Server-timing collects backend-call spans into the
  // response header (SSR document + server-fn RPCs).
  requestMiddleware: [
    sentryGlobalRequestMiddleware,
    // Directly after Sentry so its headers also land on responses the inner middlewares
    // (CSRF rejections included) answer before the handler runs.
    securityHeadersMiddleware,
    requestLogMiddleware,
    sentryUserMiddleware,
    csrfMiddleware,
    serverTimingMiddleware,
    // A server fn id from a previous build never reaches the function-level skew guard.
    unknownServerFnMiddleware,
  ],
  // A bare proxy/Node error page becomes a typed ServerUnavailableError; the skew guard reloads
  // stale tabs before old code runs; tenant middleware adds school headers. The log middleware
  // sits after tenant so its line carries the school.
  functionMiddleware: [
    sentryGlobalFunctionMiddleware,
    serverUnavailableMiddleware,
    versionSkewMiddleware,
    tenantContextMiddleware,
    serverFnLogMiddleware,
    notFoundMiddleware,
  ],
}));
